import { useEffect, useState } from 'react';
import { MEMBERSHIP_PRICE_MNT } from '@big2/shared';
import { useGameState } from '../../net/GameProvider.js';
import { useI18n } from '../../i18n.js';
import { StatusNotice } from '../StatusNotice.js';
import { usePaymentRefresh } from '../payment/usePaymentRefresh.js';
import './billing.css';

export function BillingSection() {
  const { state, actions } = useGameState();
  const { lang } = useI18n();
  const { profile } = state;
  const [couponCode, setCouponCode] = useState('');
  useEffect(() => {
    if (profile?.tier === 'paid') setCouponCode('');
  }, [profile?.tier, profile?.subscriptionExpiresAt]);
  const { refreshPayment, refreshRequest, refreshNotice } = usePaymentRefresh();

  const isMn = lang === 'mn';
  if (!profile) return null;
  const isPaidTier = profile.tier === 'paid';
  const appliedCoupon = state.couponQuote?.kind === 'paid' &&
    state.couponQuote.code === couponCode.trim().toUpperCase() ? state.couponQuote : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Unified QPay Payment Modal Box */}
      {state.paymentInfo && (
        <div style={{ background: '#fff', padding: 16, borderRadius: 12, color: '#000', textAlign: 'center', boxShadow: '0 8px 30px rgba(0,0,0,0.5)' }}>
          <h4 style={{ margin: '0 0 6px 0', fontSize: '1.1em' }}>{isMn ? 'QPay-ээр төлөх' : 'Pay with QPay'}</h4>
          {state.paymentInfo.itemDescription && (
            <div style={{ fontSize: '0.92em', fontWeight: 800, color: '#047857', marginBottom: 8 }}>
              {state.paymentInfo.itemDescription} {state.paymentInfo.amount ? `— ₮${state.paymentInfo.amount.toLocaleString()}` : ''}
            </div>
          )}
          {state.paymentInfo.qrImage ? (
            <img
              src={state.paymentInfo.qrImage.startsWith('data:') ? state.paymentInfo.qrImage : `data:image/png;base64,${state.paymentInfo.qrImage}`}
              alt={isMn ? 'QPay-ээр төлөх QR код' : 'Scan to pay with QPay'}
              width={220}
              height={220}
              style={{ maxWidth: '100%', margin: '0 auto 12px', display: 'block', borderRadius: 8, border: '1px solid #e5e7eb' }}
            />
          ) : (
            <p style={{ margin: '0 0 12px 0', fontSize: '0.82em', wordBreak: 'break-all', opacity: 0.8 }}>{state.paymentInfo.qrText}</p>
          )}
          {Array.isArray(state.paymentInfo.urls) && state.paymentInfo.urls.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center', marginBottom: 12 }}>
              {state.paymentInfo.urls.map((url) => (
                <a
                  key={url.name}
                  href={url.link}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    background: '#f3f4f6',
                    color: '#111827',
                    padding: '6px 12px',
                    borderRadius: 6,
                    fontSize: '0.82em',
                    textDecoration: 'none',
                    fontWeight: 600,
                  }}
                >
                  {url.name}
                </a>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              type="button"
              disabled={state.connectionStatus !== 'connected' || refreshRequest !== null}
              onClick={() => refreshPayment(state.paymentInfo!.invoiceId)}
              style={{ background: '#10b981', color: '#fff', fontWeight: 700 }}
            >
              {isMn ? 'Төлбөр шалгах' : 'Check payment'}
            </button>
            <button
              type="button"
              onClick={actions.clearPaymentInfo}
              style={{ background: '#6b7280', color: '#fff' }}
            >
              {isMn ? 'Хаах' : 'Close'}
            </button>
          </div>
          {state.connectionStatus !== 'connected' && <StatusNotice tone="error" message={isMn ? 'Сервертэй холбогдоогүй. Төлөв шалгах боломжгүй.' : 'Disconnected from server. Cannot refresh payment status.'} />}
          {refreshRequest && <StatusNotice tone="pending" message={isMn ? 'Төлөв шалгаж байна...' : 'Checking payment status...'} />}
          {refreshNotice && <StatusNotice {...refreshNotice} />}
        </div>
      )}
      {isPaidTier && profile.subscriptionExpiresAt && (
        <section className="vip-validity" aria-label={isMn ? 'VIP эрхийн хугацаа' : 'VIP membership validity'}>
          <div className="vip-validity__mark" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 8.5 7 12l5-7 5 7 4-3.5-2 10H5l-2-10Z" />
              <path d="M8 21h8" />
            </svg>
          </div>
          <div className="vip-validity__details">
            <div className="vip-validity__label">{isMn ? 'VIP эрх хүчинтэй' : 'VIP access until'}</div>
            <time className="vip-validity__date" dateTime={profile.subscriptionExpiresAt}>
              {new Date(profile.subscriptionExpiresAt).toLocaleString(isMn ? 'mn-MN' : 'en-US', {
                year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit',
              })}
            </time>
          </div>
          <span className="vip-validity__badge">{isMn ? 'Идэвхтэй' : 'Active'}</span>
        </section>
      )}
      {/* Account Tier Comparison & Upgrade / Switch Box */}
      <div
        style={{
          background: isPaidTier
            ? 'linear-gradient(135deg, rgba(251, 191, 36, 0.16), rgba(217, 119, 6, 0.12))'
            : 'rgba(0,0,0,0.32)',
          border: isPaidTier
            ? '1px solid rgba(251, 191, 36, 0.55)'
            : '1px solid rgba(255,255,255,0.15)',
          borderRadius: 12,
          padding: 14,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
        }}
      >
        <div style={{ fontWeight: 800, fontSize: '0.92em', color: '#fde68a' }}>
          👑 {isMn ? 'Гишүүнчлэлийн эрх (Free vs Paid Tier)' : 'Membership Tier (Free vs Paid)'}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: '0.8em' }}>
          <div
            style={{
              background: !isPaidTier ? 'rgba(74, 222, 128, 0.12)' : 'rgba(0,0,0,0.3)',
              border: !isPaidTier ? '1px solid rgba(74, 222, 128, 0.5)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 800, color: '#4ade80', marginBottom: 4 }}>
              {isMn ? 'Үнэгүй эрх (Free)' : 'Free Tier'}
            </div>
            <div>• {isMn ? '7 хоног бүр +40 🍵 Цай' : '+40 🍵 Tsai / week'}</div>
            <div>• {isMn ? 'Сонгодог хөзрийн ар' : 'Classic card back'}</div>
            <div>• {isMn ? 'Энгийн эможи' : 'Free emoji reactions'}</div>
            <div>• {isMn ? 'Ширээний шидэлт: 1 тоглоомд 1 эрх' : 'Table throw: 1 per game'}</div>
          </div>

          <div
            style={{
              background: isPaidTier ? 'rgba(251, 191, 36, 0.18)' : 'rgba(0,0,0,0.3)',
              border: isPaidTier ? '1px solid rgba(251, 191, 36, 0.65)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8,
              padding: 10,
            }}
          >
            <div style={{ fontWeight: 800, color: '#fbbf24', marginBottom: 4 }}>
              👑 {isMn ? 'Төлбөртэй эрх (Paid)' : 'Paid Tier (VIP)'}
            </div>
            <div>• <strong>{isMn ? '7 хоног бүр +160 🍵 Цай' : '+160 🍵 Tsai / week'}</strong></div>
            <div>• <strong>{isMn ? '🎯 Ширээний хөгжилтэй шидэлтүүд хязгааргүй' : '🎯 Unlimited table throwables'}</strong></div>
            <div>• <strong>{isMn ? '🗣️ Монгол дуут тавлалт, сэтгэгдлүүд нээлттэй' : '🗣️ All Mongolian voice taunts unlocked'}</strong></div>
            <div>• {isMn ? 'Бүх тусгай хөзрийн ар' : 'All custom card backs'}</div>
            <div>• {isMn ? 'Тусгай зурагтай эмоци' : 'All paid sticker emotes'}</div>
            <div>• {isMn ? 'Өрөөнд тусгай нэр өгөх' : 'Unique custom room names'}</div>
            <div>• {isMn ? 'Өрөөнд дуут яриа нээх (бүх тоглогч)' : 'Host voice chat rooms (for all guests)'}</div>
          </div>
        </div>

        {!state.paymentInfo ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button
                  type="button"
                  disabled={Boolean(couponCode.trim()) && !appliedCoupon}
                  onClick={() => actions.initiatePayment('paid', appliedCoupon?.code)}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #fbbf24)',
                    color: '#000',
                    fontWeight: 900,
                    padding: '9px 14px',
                    fontSize: '0.9em',
                  }}
                >
                  👑 {isMn
                    ? `${appliedCoupon?.months ?? 1} сар / QPay (${(appliedCoupon?.amount ?? MEMBERSHIP_PRICE_MNT).toLocaleString('mn-MN')}₮)`
                    : `${isPaidTier ? 'Renew' : 'Subscribe'}: ${appliedCoupon?.months ?? 1} ${(appliedCoupon?.months ?? 1) === 1 ? 'month' : 'months'} / ${(appliedCoupon?.amount ?? MEMBERSHIP_PRICE_MNT).toLocaleString('en-US')} MNT`}
                </button>
                {appliedCoupon && <div role="status" style={{ color: '#a7f3d0', fontSize: '0.82em' }}>
                  {isMn ? `${appliedCoupon.code} купоны үнэ тооцоологдлоо.` : `Coupon ${appliedCoupon.code} price applied.`}
                </div>}
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    type="text"
                    placeholder={isMn ? 'Купон код оруулах' : 'Enter coupon code'}
                    value={couponCode}
                    onChange={(e) => { actions.clearCouponQuote(); setCouponCode(e.target.value); }}
                    style={{ flex: 1, minWidth: 0, padding: '6px 10px', fontSize: '0.85em' }}
                  />
                  <button
                    type="button"
                    disabled={!couponCode}
                    onClick={() => actions.redeemCoupon(couponCode)}
                    style={{ padding: '6px 12px', fontSize: '0.85em' }}
                  >
                    {isMn ? 'Идэвхжүүлэх' : 'Redeem'}
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '10px 0', color: '#fde68a', fontSize: '0.85em' }}>
                ⏳ {isMn ? 'Төлбөрийн нэхэмжлэх дээр нээгдсэн байна.' : 'Payment invoice is active above.'}
              </div>
        )}
      </div>
    </div>
  );
}
